import type {
    CreateExtArgs,
    CreateExtensionArgument,
    DalValue,
    DmApis,
    Extension,
    ExtensionAPI,
    ValidatorMap
} from '@wix/document-manager-core'
import type {
    AutoGridCalculationConfig,
    AutoGridCalculationResults,
    CompRef,
    Pair,
    Pointer,
    Rect
} from '@wix/document-services-types'
import {calculateGridByRects} from './generators/autoGrid/autoGrid'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {RMApi} from '../rendererModel'
import {DATA_TYPES, VIEW_MODES} from '../../constants/constants'
import _ from 'lodash'

interface LayoutAPI extends ExtensionAPI {
    generators: {
        autoGrid: {
            generate(
                containerRect: Rect,
                childPointerRectPairs: Pair<CompRef, Rect>[],
                config?: AutoGridCalculationConfig
            ): AutoGridCalculationResults
        }
    }
}

interface ComponentsAPI extends ExtensionAPI {
    isPinned(pointer: Pointer): boolean
}

export interface LayoutExtensionApi extends ExtensionAPI {
    layout: LayoutAPI
    components: ComponentsAPI
}

const createExtension = ({experimentInstance}: CreateExtensionArgument): Extension => {
    const createValidator = ({extensionAPI, pointers}: DmApis): ValidatorMap => {
        /**
         * This validator is here for investigation only, it will be fixed on the schema level
         */
        const forbidMissingTypeInSingleDataLayout = (pointer: Pointer, value: DalValue) => {
            if (pointer.type !== DATA_TYPES.layout || value?.type !== 'SingleLayoutData') {
                return
            }

            const errors = []

            if (value.componentLayout && !_.isEmpty(value.componentLayout) && !value.componentLayout.type) {
                errors.push({
                    shouldFail: experimentInstance.isOpen('dm_failOnMalformedSingleDataLayout'),
                    type: 'missingTypeInComponentLayout',
                    message: 'SingleLayoutData componentLayout is missing required type property',
                    extras: {
                        pointer,
                        value: value.componentLayout,
                        layoutType: 'componentLayout'
                    }
                })
            }

            if (value.containerLayout && !_.isEmpty(value.containerLayout) && !value.containerLayout.type) {
                errors.push({
                    shouldFail: experimentInstance.isOpen('dm_failOnMalformedSingleDataLayout'),
                    type: 'missingTypeInContainerLayout',
                    message: 'SingleLayoutData containerLayout is missing required type property',
                    extras: {
                        pointer,
                        value: value.containerLayout,
                        layoutType: 'containerLayout'
                    }
                })
            }

            if (value.itemLayout && !_.isEmpty(value.itemLayout) && !value.itemLayout.type) {
                errors.push({
                    shouldFail: experimentInstance.isOpen('dm_failOnMalformedSingleDataLayout'),
                    type: 'missingTypeInItemLayout',
                    message: 'SingleLayoutData itemLayout is missing required type property',
                    extras: {
                        pointer,
                        value: value.itemLayout,
                        layoutType: 'itemLayout'
                    }
                })
            }

            return errors.length > 0 ? errors : undefined
        }

        const validateBuilderComponentLayoutQuery = (pointer: Pointer, value: DalValue) => {
            if (!value || pointer.type !== VIEW_MODES.DESKTOP || pointers.structure.isMasterPage(pointer)) {
                return
            }

            const {rendererModel} = extensionAPI as RMApi

            if (!rendererModel.isBuilderComponentModel()) {
                return
            }

            if (!value.layoutQuery) {
                return [
                    {
                        shouldFail: experimentInstance.isOpen('dm_failOnMissingLayoutQuery'),
                        type: 'missingLayoutQueryForComponent',
                        message: `Component ${pointer.id} is missing layout`,
                        extras: {
                            pointer,
                            componentType: value.componentType
                        }
                    }
                ]
            }
        }

        return {
            forbidMissingTypeInSingleDataLayout,
            validateBuilderComponentLayoutQuery
        }
    }

    const createExtensionAPI = ({extensionAPI}: CreateExtArgs): LayoutExtensionApi => {
        return {
            layout: {
                generators: {
                    autoGrid: {
                        generate: (
                            containerRect: Rect,
                            childPointerRectPairs: Pair<CompRef, Rect>[],
                            config?: AutoGridCalculationConfig
                        ) => {
                            const results = calculateGridByRects(
                                containerRect,
                                Object.fromEntries(
                                    childPointerRectPairs.map(([compPointer, rect]) => [compPointer.id, rect])
                                ),
                                config
                            )

                            const {componentsLayouts, containerLayout} = results

                            return {
                                containerLayout,
                                componentsLayouts: childPointerRectPairs.map(([compPointer]) => [
                                    compPointer,
                                    componentsLayouts[compPointer.id]
                                ])
                            }
                        }
                    }
                }
            },

            components: {
                isPinned: (compPointer: Pointer) => {
                    const layout = (extensionAPI as VariantsExtensionAPI).variants.getComponentItemConsideringVariants(
                        compPointer,
                        DATA_TYPES.layout
                    )
                    return (
                        layout?.itemLayout?.type === 'FixedItemLayout' || layout?.itemLayout?.pinnedToContainer === true
                    )
                }
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {layout} = extensionAPI as LayoutExtensionApi

        return {
            layout: {
                generators: {
                    autoGrid: {
                        generate: layout.generators.autoGrid.generate
                    }
                }
            }
        }
    }
    return {
        name: 'layout',
        createExtensionAPI,
        createPublicAPI,
        createValidator
    }
}

export {createExtension}
